/**
 * 13 #10 — Matrix view of the All Drawings tab: one card per project, rows = Block / Floor from the basement up
 * to the top roof, columns = disciplines (BMS, LCS, HA …), each cell coloured by the status of its drawing (the
 * least advanced one when a floor has several). A cell opens its drawing; a cell with several lists them first.
 * Uses the same filters as the list (search, Project, Discipline, Status …).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { MatrixGrid } from '@/components/ui/matrix-grid';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusPill } from '@/components/ui/status-pill';
import { formatNumber } from '@/lib/format';
import { getStatusTone } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  PIPELINE_GROUPS,
  STATUS_LABELS,
  bottleneckOf,
  buildMatrix,
  disciplinesOf,
  drawingTitle,
  labelize,
  matrixLabel,
  mostCommon,
  projectNumber,
  projectSummary,
  valueKey,
} from './model';
import { DrawingIdText, GroupTitle, MiniStackBar, segmentFill } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const FIRST_PROJECTS = 6;

function MatrixLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-ink-2" data-testid="matrix-legend">
      <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Cell colour</span>
      {PIPELINE_GROUPS.filter((group) => !group.optional).map((group) => (
        <span key={group.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-3.5 rounded-[3px]" style={{ background: segmentFill(group) }} aria-hidden="true" />
          {group.short || group.label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5 text-ink-3">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--status-gray)]" aria-hidden="true" /> no drawing
      </span>
    </div>
  );
}

function ProjectMatrix({ project, onCell, projectHref }) {
  const matrix = useMemo(() => buildMatrix(project.drawings), [project.drawings]);
  const summary = useMemo(() => projectSummary(project.drawings), [project.drawings]);
  const href = projectHref?.(project.label);
  return (
    <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid={`matrix-project-${project.key}`}>
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line-2 bg-surface-2 px-3.5 py-2.5">
        <GroupTitle label={project.label} number={project.number} noNumberText="no project no." discs={disciplinesOf(project.drawings)} count={`· ${fmt(project.drawings.length)} ${project.drawings.length === 1 ? 'drawing' : 'drawings'}`} meta={`· ${matrix.rows.length} ${matrix.rows.length === 1 ? 'floor / zone' : 'floors / zones'}`} />
        <span className="ml-auto flex items-center gap-2.5">
          {href ? (
            <Link to={href} className="grid h-7 w-7 place-items-center rounded-lg text-ink-3 hover:bg-surface-hover hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`Open project ${project.label}`} title={`Open project ${project.label}`}>
              <FolderOpen className="h-[15px] w-[15px]" aria-hidden="true" />
            </Link>
          ) : null}
          <MiniStackBar segments={summary.segments} label={`${summary.approved} of ${summary.total} approved`} className="hidden sm:flex" />
          <span className={cn('min-w-[92px] text-right text-[12.5px] font-bold tabular-nums', summary.approvedPct ? 'text-ink' : 'text-ink-3')}>{summary.approvedPct}% approved</span>
        </span>
      </header>
      <div className="px-3 py-3">
        <MatrixGrid
          ariaLabel={`${project.label}: drawing status by floor and discipline`}
          rowHeader="Block / Floor"
          rows={matrix.rows}
          columns={matrix.columns}
          cellWidth={matrix.columns.length <= 3 ? 150 : matrix.columns.length <= 5 ? 124 : 104}
          dense={matrix.rows.length > 14}
          testId={`matrix-${project.key}`}
          getCell={(row, column) => {
            const drawings = matrix.cell(row.key, column.key);
            if (!drawings.length) return { empty: true, title: `${row.label} · ${column.label}: no drawing`, disabled: true };
            const worst = bottleneckOf(drawings);
            const counts = new Map();
            drawings.forEach((drawing) => counts.set(drawing.status, (counts.get(drawing.status) || 0) + 1));
            const breakdown = [...counts.entries()].map(([status, count]) => `${count} ${labelize(status, STATUS_LABELS)}`).join(', ');
            return {
              tone: getStatusTone(worst.status, 'shop_drawing'),
              label: drawings.length === 1 ? matrixLabel(worst.status) : `${drawings.length} · ${matrixLabel(worst.status)}`,
              title: drawings.length === 1
                ? `${row.label} · ${column.label}: ${drawingTitle(worst)} — ${labelize(worst.status, STATUS_LABELS)}`
                : `${row.label} · ${column.label}: ${drawings.length} drawings (${breakdown})`,
            };
          }}
          onCellClick={(row, column) => onCell(project, row, column, matrix.cell(row.key, column.key))}
        />
      </div>
    </section>
  );
}

export function DrawingMatrix({ rows, loading, onOpen, projectHref, onClearFilters, filtered }) {
  const [shown, setShown] = useState(FIRST_PROJECTS);
  const [cell, setCell] = useState(null);
  const projects = useMemo(() => {
    const map = new Map();
    rows.forEach((drawing) => {
      const key = valueKey(drawing.project) || '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drawing);
    });
    return [...map.entries()]
      .map(([key, drawings]) => ({ key, drawings, label: mostCommon(drawings, (drawing) => drawing.project) || 'No project', number: mostCommon(drawings, projectNumber) }))
      .sort((a, b) => b.drawings.length - a.drawings.length || a.label.localeCompare(b.label));
  }, [rows]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="drawing-matrix-loading">
        {[0, 1].map((index) => <Skeleton key={index} className="h-56 w-full rounded-card" />)}
      </div>
    );
  }
  if (!projects.length) {
    return (
      <div className="rounded-card border border-line bg-surface">
        <EmptyState
          icon={LayoutGrid}
          titleAs="p"
          title={filtered ? 'No drawings match these filters' : 'No shop drawings yet'}
          text={filtered ? 'Try a different search, or clear the filters.' : 'Add drawings to see them by floor and discipline.'}
          action={filtered ? <Button type="button" variant="outline" size="sm" onClick={onClearFilters}>Clear filters</Button> : null}
        />
      </div>
    );
  }

  const openCell = (project, row, column, drawings) => {
    if (drawings.length === 1) onOpen(drawings[0]);
    else if (drawings.length > 1) setCell({ project, row, column, drawings });
  };

  return (
    <div className="space-y-3" data-testid="drawing-matrix">
      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <MatrixLegend />
        <span className="text-[12px] text-ink-3">Floors run from the basement up · a cell with several drawings takes the colour of the least advanced one</span>
      </div>
      {projects.slice(0, shown).map((project) => (
        <ProjectMatrix key={project.key} project={project} onCell={openCell} projectHref={project.key !== '__none__' ? projectHref : null} />
      ))}
      {projects.length > shown ? (
        <div className="flex items-center justify-center gap-3 py-1">
          <span className="text-[12.5px] text-ink-3">Showing {fmt(shown)} of {fmt(projects.length)} projects</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setShown((value) => value + FIRST_PROJECTS)} data-testid="matrix-show-more">
            Show {fmt(Math.min(FIRST_PROJECTS, projects.length - shown))} more
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setShown(projects.length)}>Show all</Button>
        </div>
      ) : null}

      <Dialog open={Boolean(cell)} onOpenChange={(open) => { if (!open) setCell(null); }}>
        <DialogContent className="max-w-lg rounded-2xl border-line bg-surface text-ink" data-testid="matrix-cell-dialog">
          {cell ? (
            <>
              <DialogHeader className="text-left">
                <DialogTitle className="font-heading text-[16px] font-bold text-ink">{cell.row.label} · {cell.column.label}</DialogTitle>
                <DialogDescription className="text-[12.5px] text-ink-3">{cell.project.label}{cell.row.sublabel ? ` · ${cell.row.sublabel}` : ''} · {fmt(cell.drawings.length)} drawings</DialogDescription>
              </DialogHeader>
              <ul className="divide-y divide-line-2 overflow-hidden rounded-xl border border-line">
                {cell.drawings.map((drawing) => (
                  <li key={drawing.id}>
                    <button
                      type="button"
                      onClick={() => { setCell(null); onOpen(drawing); }}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-ink">{drawingTitle(drawing)}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-ink-3">
                          <DrawingIdText value={drawing.drawingId} /> <span aria-hidden="true">·</span> <span className="font-mono text-ink-4">{drawing.id}</span>
                        </span>
                      </span>
                      <StatusPill status={drawing.status} domain="shop_drawing" label={labelize(drawing.status, STATUS_LABELS)} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
